// These types mirror the server's frozen reporting contract. Permission and scope decisions
// belong to the API; no actor identifier is accepted from the page.
export interface ReportCapability {
  defaultOwnerId: string; canView: boolean; canSelectOwners: boolean; canPreview: boolean
  canGenerate: boolean; canSubmit: boolean; canInitialize: boolean; isAvailable: boolean; disabledReason: string | null
}
export interface ReportOwner { userId: string; displayName: string; isActive: boolean }
export interface OwnerSelection { all?: boolean; ownerIds?: string[] }
export interface BaselineReference { reportId: string; recipientKey: string }
export interface ReportRequest {
  owners?: OwnerSelection; amountChangesOnly?: boolean; fromBusinessDate?: string; throughBusinessDate?: string; baseline?: BaselineReference
}
export interface ReportPayment { benefitId: string; programId: number; programYear: number; vendorId: number; recipientKey: string }
export interface ReportSnapshot {
  payment: ReportPayment; recipientName: string; worksheet: string; amount: number; accountNumber: string
  applicantFullName: string; residenceAddress: string; applicantNameOnBill: string; urgent: boolean; updated: boolean; calledIn: boolean
  fileNumber: string; fundingSourceName: string; pledgeState: string; grantAmount: number; vendorName: string; ownerId: string | null; ownerName: string
}
export interface ReportChange {
  payment: ReportPayment; kind: string; expectedSubmissionSequence: number; baselineAmount: number; resultingAmount: number; delta: number
  baseline: ReportSnapshot | null; result: ReportSnapshot | null
}
export interface ReportPreview {
  cutoffAt: string; timeZone: string; ownerIds: string[]; includesUnassigned: boolean; amountChangesOnly: boolean
  fromBusinessDate: string | null; throughBusinessDate: string | null; baseline: string; lastSubmissionSequence: number | null
  changes: ReportChange[]; suppressedAccountChangeCount: number; deferredPaymentCount: number; dateExcludedPaymentCount: number
  issues: { benefitId: string; programId: number; programYear: number; vendorId: number; code: string }[]
}
export interface ReportGroup {
  recipientKey: string; recipientName: string; worksheet: string; rowCount: number; submissionSequence: number | null
  submittedAt: string | null; submittedBy: string | null; isStale: boolean; recipientWorkbookSha256: string | null
}
export interface FrozenReport {
  reportId: string; createdBy: string; creatorName: string; generatedAt: string; mode: string; status: string
  workbookSha256: string; frozen: ReportPreview; groups: ReportGroup[]; isStale: boolean
}
export interface Checkpoint extends BaselineReference {
  recipientName: string; sequence: number; generatedAt: string; submittedAt: string; submittedBy: string; initialReconciliation: boolean
}
export interface CheckpointHistory { checkpoints: Checkpoint[]; next: { sequence: number; recipientKey: string } | null }
export interface ReportReference {
  reportId: string; mode: string; generatedAt: string; createdBy: string; canReadWholeReport: boolean; groups: ReportGroup[]; changes: ReportChange[]
}
export interface ReferenceHistory { timeZone: string; reports: ReportReference[]; next: { generatedAt: string; reportId: string } | null }
export interface SubmissionReceipt {
  operationId: string; reportId: string; sequence: number; recordedBy: string; recordedAt: string; initialReconciliation: boolean; reason: string | null
}
export const reportRoot = "/api/pledge-reports"
export const excelType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
export const pacificTime = (value: string | null) => value ? new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles", dateStyle: "medium", timeStyle: "long",
}).format(new Date(value)) : "Not submitted"
export const reportMode = (mode: string) => mode === "ComparisonOnly" ? "Comparison only" : mode === "InitialReconciliation" ? "Initial reconciliation" : "Operational report"
export const money = (amount: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
export const paymentKey = (payment: ReportPayment) => JSON.stringify([payment.benefitId, payment.programId, payment.programYear, payment.vendorId, payment.recipientKey])
